import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, Play, MapPin, X, Search, Mic } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { ListingCard } from "./ListingCard";
import AcquereurModal from "./AcquereurModal";

import { parseVideo } from "./MediaBackground";
import { Brand } from "./Brand";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { WhyOffMarket } from "./WhyOffMarket";
import { fetchListings, fetchFacets, fetchMembers, fmtPrice, VINCENT_STUDIO } from "./api";

function AnimatedNumber({ target, suffix = "", prefix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1300;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{prefix}{n}{suffix}</>;
}

const heroContainer = {
  hidden: {},
  visible: (delay = 0) => ({ transition: { staggerChildren: 0.03, delayChildren: delay } }),
};
const heroLetter = {
  hidden: { opacity: 0, y: "0.5em" },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};
const Letters = ({ text, className = "" }) =>
  Array.from(text).map((ch, i) => (
    <motion.span key={i} variants={heroLetter} className={className} style={{ display: "inline-block", whiteSpace: "pre" }} aria-hidden="true">
      {ch}
    </motion.span>
  ));

// Fondu chic et rapide, identique pour tous les changements de photo.
const FADE_VARIANT = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.9, ease: "easeInOut" } };

function heroVideoSrc(url) {
  const v = parseVideo(url);
  if (!v) return null;
  if (v.type === "youtube") return { type: "iframe", src: `https://www.youtube.com/embed/${v.id}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${v.id}&modestbranding=1` };
  if (v.type === "vimeo") return { type: "iframe", src: `https://player.vimeo.com/video/${v.id}?autoplay=1&muted=1&loop=1&background=1` };
  if (v.type === "file") return { type: "file", src: v.url };
  return null;
}

const hModeBtn = (active) => `flex-1 inline-flex items-center justify-center gap-1 text-[11px] uppercase tracking-tight font-semibold px-1.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${active ? "bg-[#C19A6B] border-[#C19A6B] text-white" : "border-white/25 text-white/80 hover:border-[#C19A6B]/60 hover:text-[#C19A6B]"}`;

function HeroSlider({ slides, index, override }) {
  const v = FADE_VARIANT;
  const current = slides[index];
  if (!current) return null;
  if (override && override.video) {
    const emb = heroVideoSrc(override.video);
    if (emb) {
      return (
        <div className="absolute inset-0 overflow-hidden bg-black" data-testid="hero-media-video">
          {emb.type === "file"
            ? <video key={emb.src} src={emb.src} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
            : <iframe key={emb.src} src={emb.src} title="Vidéo" className="absolute inset-0 w-[130%] h-[130%] -left-[15%] -top-[15%]" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" />}
        </div>
      );
    }
  }
  const bg = (override && override.img) || current.cover;
  return (
    <div className="absolute inset-0 overflow-hidden">
      <AnimatePresence mode="popLayout">
        <motion.div
          key={(override && override.key ? override.key : current.slug) + "-" + index}
          initial={v.initial} animate={v.animate} exit={v.exit} transition={v.transition}
          className="absolute inset-0 bg-cover bg-center will-change-transform"
          style={{ backgroundImage: `url(${bg})` }}
        />
      </AnimatePresence>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [hero, setHero] = useState(null);
  const [advisors, setAdvisors] = useState(0);
  const [departments, setDepartments] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [heroMediaIdx, setHeroMediaIdx] = useState(0);
  const [heroMode, setHeroMode] = useState("cover"); // cover | photos | ai | video
  const [heroPaused, setHeroPaused] = useState(false);
  const [userGeo, setUserGeo] = useState(null);
  const [geoPrompt, setGeoPrompt] = useState(false);
  const [acqOpen, setAcqOpen] = useState(false);
  const nav = useNavigate();
  const openAcq = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 768) setAcqOpen(true);
    else nav("/espace-acquereurs");
  };

  const askGeo = () => {
    try { localStorage.setItem("omk_geo_choice", "granted"); } catch (_) {}
    setGeoPrompt(false);
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    }
  };
  const dismissGeo = () => {
    try { localStorage.setItem("omk_geo_choice", "declined"); } catch (_) {}
    setGeoPrompt(false);
  };

  useEffect(() => {
    fetchListings({ sort: "featured" }).then((data) => {
      setListings(data);
    });
    fetchMembers({}).then((m) => setAdvisors(Array.isArray(m) ? m.length : 0));
    fetchFacets().then((f) => setDepartments((f.departments || []).length));
    // Utilisateur de passage : proposer un pop-up personnalisé pour afficher les biens proches
    let choice = null;
    try { choice = localStorage.getItem("omk_geo_choice"); } catch (_) {}
    if (choice === "granted" && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    } else if (choice !== "declined") {
      const t = setTimeout(() => setGeoPrompt(true), 6000);
      return () => clearTimeout(t);
    }
  }, []);

  const geoDist = (l) => {
    if (!userGeo || l.lat == null || l.lng == null) return Infinity;
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(l.lat - userGeo.lat), dLng = toRad(l.lng - userGeo.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(userGeo.lat)) * Math.cos(toRad(l.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const featured = listings.filter((l) => l.featured);
  const biens = listings.length;
  // Meilleurs matchs d'abord ; si géolocalisé, on priorise les biens proches
  const ordered = React.useMemo(() => {
    const base = featured.length ? featured : listings;
    if (!userGeo) return base;
    return [...base].sort((a, b) => geoDist(a) - geoDist(b));
  }, [listings, userGeo]); // eslint-disable-line
  // Annonces récentes : priorité aux biens publiés depuis < 7 jours, extension à 14 j si trop peu.
  const recents = React.useMemo(() => {
    if (!listings.length) return [];
    const byDate = [...listings].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    const daysSince = (l) => l.created_at ? (Date.now() - new Date(l.created_at).getTime()) / 86400000 : Infinity;
    let r = byDate.filter((l) => daysSince(l) <= 7);
    if (r.length < 3) r = byDate.filter((l) => daysSince(l) <= 14);
    if (r.length < 3) r = byDate;
    return r.slice(0, 6);
  }, [listings]);
  const heroSlides = ordered.slice(0, 8);
  const current = heroSlides[slideIdx] || hero;
  const curDep = (current && current.department) || "";
  const curDepNum = (curDep.match(/(\d{2,3})/) || [])[1];
  const curDepName = curDep.replace(/\s*\(.*?\)\s*/g, "").trim();
  const curPhotos = ((current && current.images && current.images.length ? current.images : [current && current.cover]) || []).filter(Boolean);
  const curAi = ((current && current.staging) || []).map((s) => s.after).filter(Boolean);
  const curVideo = (current && (current.video_url || current.hero_video)) || "";
  const heroOverride =
    heroMode === "photos" ? { img: curPhotos[heroMediaIdx % Math.max(curPhotos.length, 1)], key: "photos" } :
    heroMode === "ai" ? { img: curAi[heroMediaIdx % Math.max(curAi.length, 1)], key: "ai" } :
    heroMode === "video" ? { video: curVideo, key: "video" } : null;
  const heroMobileImg =
    heroMode === "ai" ? (curAi[heroMediaIdx % Math.max(curAi.length, 1)] || (current && current.cover)) :
    heroMode === "photos" ? (curPhotos[heroMediaIdx % Math.max(curPhotos.length, 1)] || (current && current.cover)) :
    (current && (current.cover || (current.images && current.images[0])));
  // Réinitialise le média du hero quand on change de slide
  useEffect(() => { setHeroMode("cover"); }, [slideIdx]);

  // Lot A : défilement auto des photos/staging quand on survole les boutons média
  useEffect(() => {
    const len = heroMode === "photos" ? curPhotos.length : heroMode === "ai" ? curAi.length : 0;
    if (len < 2) return;
    const t = setInterval(() => setHeroMediaIdx((i) => i + 1), 1600);
    return () => clearInterval(t);
  }, [heroMode, curPhotos.length, curAi.length]);

  // Slider auto (7s) en boucle
  useEffect(() => {
    if (heroSlides.length < 2 || heroPaused) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % heroSlides.length), 7000);
    return () => clearInterval(t);
  }, [heroSlides.length, heroPaused]);
  const goPrev = () => setSlideIdx((i) => (i - 1 + heroSlides.length) % heroSlides.length);
  const goNext = () => setSlideIdx((i) => (i + 1) % heroSlides.length);

  const STATS = [
    { title: "BIENS OFF MARKET", node: <AnimatedNumber target={biens} />, label: t("stat_listings") },
    { title: "RÉSEAU", node: <AnimatedNumber target={advisors} />, label: t("stat_network") },
    { title: "EXPÉRIENCE", node: <AnimatedNumber target={15} prefix="+" suffix={t("years")} />, label: t("stat_experience") },
    { title: "COUVERTURE", node: <AnimatedNumber target={departments} />, label: departments > 1 ? t("stat_departments") : t("stat_department") },
    { title: "DISPONIBILITÉ", node: <>{t("availability_value")}</>, label: t("stat_availability") },
    { title: "COMMISSION", node: <>4 à 7%</>, label: t("stat_commission") },
  ];

  return (
    <PlatformLayout>
      {/* HERO dynamique */}
      <section data-testid="home-hero" className="relative min-h-[92vh] flex items-end overflow-hidden bg-black">
        {heroSlides.length > 0 && <div className="hidden lg:block"><HeroSlider slides={heroSlides} index={slideIdx} override={heroOverride} /></div>}
        <div className="absolute inset-0 hidden lg:block" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.92), rgba(5,5,5,0.25))" }} />

        {heroSlides.length > 1 && (
          <>
            <button onClick={goPrev} data-testid="hero-prev" aria-label="Précédent" className="absolute -left-1 sm:left-1 lg:left-6 top-1/2 -translate-y-1/2 z-20 text-white hover:text-[#C19A6B] transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"><ChevronLeft className="w-9 h-9 sm:w-10 sm:h-10" strokeWidth={2} /></button>
            <button onClick={goNext} data-testid="hero-next" aria-label="Suivant" className="absolute -right-1 sm:right-1 lg:right-6 top-1/2 -translate-y-1/2 z-20 text-white hover:text-[#C19A6B] transition-colors drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"><ChevronRight className="w-9 h-9 sm:w-10 sm:h-10" strokeWidth={2} /></button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-dots">
              {heroSlides.map((_, i) => (
                <button key={i} onClick={() => setSlideIdx(i)} aria-label={`Aller à ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-500 ${i === slideIdx ? "w-8 bg-[#C19A6B]" : "w-3 bg-white/40 hover:bg-white/70"}`} />
              ))}
            </div>
          </>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pb-8 lg:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-10 items-end">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: heroMode === "cover" ? 1 : 0, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} style={{ pointerEvents: heroMode === "cover" ? "auto" : "none" }} className="lg:col-span-8 space-y-3 lg:space-y-5">
              <style>{`@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');.hp-calli{font-family:'Dancing Script',cursive;font-weight:700;text-transform:none !important;letter-spacing:0 !important}`}</style>
              <span className="hp-calli hidden lg:block mt-6 lg:mt-10 text-[1rem] sm:text-[1.15rem] lg:text-[1.35rem] leading-tight text-[#C19A6B]"><span className="text-white">{t("tagline_a")}</span>{t("tagline_b")} <span className="text-white font-bold">{t("tagline_c")}</span></span>
              <h1 data-testid="hero-title" aria-label={`${t("title_l1")} ${t("title_l2")}`} className="sr-only">{t("title_l1")} {t("title_l2")}</h1>
              <motion.p aria-hidden="true" variants={heroContainer} initial="hidden" animate="visible" custom={0.3} className="font-light tracking-tight leading-[1.12] mt-0 sm:mt-5">
                <Letters text={t("title_l1")} className="text-[1.05rem] sm:text-[2.025rem] lg:text-[2.7rem] font-normal text-white/90" />
                <br />
                <Letters text={t("title_l2")} className="text-[1.3rem] sm:text-[2.7rem] lg:text-[3.375rem] italic text-[#C19A6B]" />
              </motion.p>
            </motion.div>

            {current && (
              <motion.div key={current.slug} initial={{ opacity: 0, y: 30, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -6, scale: 1.02 }} onMouseEnter={() => setHeroPaused(true)} onMouseLeave={() => { setHeroPaused(false); setHeroMode("cover"); }} className="lg:col-span-4">
                <div className="max-lg:border max-lg:border-white/15 max-lg:rounded-[5px] max-lg:overflow-hidden max-lg:shadow-2xl">
                {(current.cover || (current.images && current.images[0])) && (
                  <img src={heroMobileImg} alt={current.title} data-testid="hero-mobile-photo" className="lg:hidden w-full aspect-[16/9] object-cover transition-opacity duration-500" />
                )}
                <Link
                  to={`/bien/${current.slug}`}
                  data-testid="hero-featured-link"
                  className="group block relative overflow-visible lg:rounded-xl lg:border lg:border-[#C19A6B]/35 bg-white/[0.055] backdrop-blur-2xl ring-1 ring-[#C19A6B]/10 p-3 sm:p-5 lg:shadow-[0_18px_60px_-20px_rgba(0,0,0,0.7)] hover:border-[#C19A6B]/60 hover:shadow-[0_28px_80px_-18px_rgba(193,154,107,0.4)] transition-[box-shadow,border-color] duration-500"
                >
                  <motion.div
                    aria-hidden="true"
                    className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#C19A6B]/25 blur-3xl pointer-events-none"
                    animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0.9, 0.55] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <div aria-hidden="true" className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/12 to-transparent pointer-events-none rounded-xl overflow-hidden" />
                  <span className="hidden lg:inline-flex absolute -top-3 left-5 z-20 items-center gap-1.5 px-3 py-1 rounded-full bg-[#C19A6B] text-white text-[10px] font-semibold uppercase tracking-[0.15em] shadow-lg shadow-black/40">
                    <Sparkles className="w-3 h-3" /> {t("featured_badge")}
                  </span>
                  <div className="lg:mt-2 flex items-start justify-between gap-3">
                    <h2 className="text-base sm:text-lg font-light text-white leading-snug group-hover:text-[#C19A6B] transition-colors">
                      {(() => {
                        const words = (current.title || "").trim().split(/\s+/);
                        const mid = Math.ceil(words.length / 2);
                        const l1 = words.slice(0, mid).join(" ");
                        const l2 = words.slice(mid).join(" ");
                        return <><span className="font-normal text-white">{l1}</span>{l2 && <><br /><span className="text-[#C19A6B] font-semibold italic">{l2}</span></>}</>;
                      })()}
                    </h2>
                    {curDepNum && (
                      <div className="shrink-0 flex flex-col items-center text-center leading-none text-zinc-300 pt-0.5">
                        <span className="text-[9px] uppercase tracking-wider">{t("sector")}</span>
                        <span className="text-3xl font-semibold text-[#C19A6B] leading-none my-0.5">{curDepNum}</span>
                        {curDepName && <span className="text-[11px] font-bold text-white uppercase tracking-wider">{curDepName}</span>}
                      </div>
                    )}
                  </div>
                  {current.owner ? (
                    <div className="mt-4 flex items-end justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {(current.owner.photo || current.owner.logo) ? (
                          <img src={current.owner.photo || current.owner.logo} alt={current.owner.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#C19A6B] shrink-0 shadow-md" />
                        ) : (
                          <span className="w-12 h-12 rounded-full bg-[#050505] border-2 border-[#C19A6B] flex items-center justify-center text-[#C19A6B] text-base font-semibold shrink-0">{(current.owner.name || "?").charAt(0)}</span>
                        )}
                        <div className="leading-tight min-w-0">
                          <p className="calli text-lg text-[#C19A6B] leading-none">{t("your_advisor")}</p>
                          <p className="text-sm font-medium text-white truncate mt-0.5">{current.owner.name}</p>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end text-right leading-tight">
                        <span className="text-xl sm:text-2xl font-bold text-white leading-none">{fmtPrice(current.price)}</span>
                        <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-[#C19A6B]">{t("see_listing")} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></span>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-end justify-between gap-2">
                      <span className="text-xl sm:text-2xl font-bold text-white leading-none">{fmtPrice(current.price)}</span>
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[#C19A6B]">
                        {t("see_listing")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  )}
                  {(curAi.length > 0 || curVideo) && (
                    <div className="mt-4 max-lg:mt-2 flex items-stretch gap-1.5" data-testid="hero-media-modes">
                      <button onMouseEnter={() => { setHeroMode("photos"); setHeroMediaIdx(0); }} onFocus={() => setHeroMode("photos")} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHeroMode((m) => { if (m === "photos") { setHeroMediaIdx((i) => i + 1); return m; } setHeroMediaIdx(0); return "photos"; }); }} data-testid="hero-mode-photos" className={hModeBtn(heroMode === "photos" || heroMode === "cover")}><ImageIcon className="w-3 h-3" /> {t("real_photos")}</button>
                      {curAi.length > 0 && <button onMouseEnter={() => { setHeroMode("ai"); setHeroMediaIdx(0); }} onFocus={() => setHeroMode("ai")} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHeroMode((m) => { if (m === "ai") { setHeroMediaIdx((i) => i + 1); return m; } setHeroMediaIdx(0); return "ai"; }); }} data-testid="hero-mode-ai" className={hModeBtn(heroMode === "ai")}><Sparkles className="w-3 h-3" /> {t("ai_staging")}</button>}
                      {curVideo && <button onMouseEnter={() => setHeroMode("video")} onFocus={() => setHeroMode("video")} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHeroMode("video"); }} data-testid="hero-mode-video" className={hModeBtn(heroMode === "video")}><Play className="w-3 h-3" /> {t("video")}</button>}
                    </div>
                  )}
                </Link>
                </div>
              </motion.div>
            )}
            <div className="lg:hidden col-span-full mt-4 mb-3">
              <span className="hp-calli block text-center text-[0.95rem] leading-tight text-[#C19A6B] whitespace-nowrap"><span className="text-white">{t("tagline_a")}</span>{t("tagline_b")} <span className="text-white font-bold">{t("tagline_c")}</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* Bande de statistiques — fond blanc, sans contour */}
      <section className="bg-white" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="px-5 py-10 flex flex-col items-center text-center min-h-[150px]"
            >
              <div className="flex items-center justify-center h-12 font-light text-[#C19A6B] leading-none text-3xl sm:text-4xl">{s.node}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mt-3 leading-snug">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Annonces récentes */}
      <section className="bg-zinc-100 py-20 sm:py-28" data-testid="home-featured">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="flex items-end justify-between">
            <div>
              <span className="calli text-2xl sm:text-3xl text-zinc-500">{t("featured_eyebrow")}</span>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-1 text-[#C19A6B]">{t("featured_title")}</h2>
            </div>
            <Link to="/biens-off-market" className="hidden sm:inline-flex items-center gap-2 text-sm text-[#C19A6B] hover:gap-3 transition-all">{t("all_listings")} <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recents.map((l, i) => <ListingCard key={l.slug} listing={l} index={i} recency />)}
          </div>
        </div>
      </section>

      {/* Pourquoi acheter en Off Market ? — concept off market côté acquéreur */}
      <WhyOffMarket accent="#C19A6B" audience="buyer" />

      {/* CTA acquéreur — recherche vocale IA (au-dessus de L'univers OFF MARKETTE) */}
      <section data-testid="home-cta-buyer" className="relative overflow-hidden min-h-[380px] flex items-center">
        <div className="absolute inset-0 bg-cover" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1609885728169-8737a953a103?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)`, backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(5,5,5,0.72), rgba(5,5,5,0.78))" }} />
        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center flex flex-col items-center space-y-6 py-16">
          <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight" text={t("buyer_title")} />
          <p className="text-zinc-300 font-light leading-relaxed">{t("buyer_text")}</p>
          <button onClick={openAcq} data-testid="home-cta-buyer-btn" className="inline-flex flex-col items-center leading-tight bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider px-8 py-3 rounded-[5px] transition-colors">
            <span className="flex items-center gap-2 text-sm"><Mic className="w-4 h-4" /> {t("buyer_btn")}</span>
            <span className="text-[10px] tracking-[0.25em] opacity-80">{t("buyer_sub")}</span>
          </button>
        </motion.div>
      </section>

      {/* Découvrir OFF MARKETTE — résumés (fond blanc, cartes gris clair) */}
      <section className="bg-white text-[#050505] py-20 sm:py-28" data-testid="home-summaries">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-lg text-[#C19A6B]">{t("discover_eyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-1">{t("discover_title")} <Brand /></h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: VINCENT_STUDIO, pos: "center 20%", tag: t("card1_tag"), title: t("card1_title"), text: t("card1_text"), to: "/nos-conseillers-et-agents-immobilier" },
              { img: "https://images.unsplash.com/photo-1558454510-fe854792a6e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", pos: "center", tag: t("card2_tag"), title: "OFF MARKETTE", brand: true, text: t("card2_text"), to: "/philosophie" },
              { img: "https://images.unsplash.com/photo-1772119065264-490a6ff55e63?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", pos: "center", tag: t("card3_tag"), title: t("card3_title"), text: t("card3_text"), to: "/expertises" },
            ].map((c, i) => (
              <motion.div key={c.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: i * 0.14, ease: [0.22, 1, 0.36, 1] }} className="h-full">
                <Link to={c.to} data-testid={`summary-${i}`} className="group flex flex-col h-full bg-zinc-100 border border-zinc-200 hover:border-[#C19A6B]/50 shadow-sm hover:shadow-md transition-all">
                  <div className="h-52 overflow-hidden shrink-0">
                    <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ objectPosition: c.pos }} />
                  </div>
                  <div className="p-6 flex flex-col flex-1 space-y-3">
                    <span className="calli text-base text-[#C19A6B]">{c.tag}</span>
                    <h3 className="text-xl font-semibold text-[#050505] uppercase tracking-wide">{c.brand ? <Brand /> : c.title}</h3>
                    <p className="text-sm font-light text-zinc-600 leading-relaxed flex-1">{c.text}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-[#C19A6B] pt-1 group-hover:gap-3 transition-all">{t("learn_more")} <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA estimation retiré (redondant avec le PreFooterCTA global) */}

      <AnimatePresence>
        {geoPrompt && (
          <motion.div
            data-testid="geo-prompt"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={dismissGeo}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg rounded-[5px] border border-white/15 bg-white/[0.07] backdrop-blur-md shadow-2xl p-5 sm:p-7 text-white overflow-hidden">
              <div aria-hidden="true" className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#C19A6B]/25 blur-3xl pointer-events-none" />
              <button onClick={dismissGeo} aria-label="Fermer" data-testid="geo-dismiss" className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              <div className="flex flex-col items-center text-center">
                <span className="shrink-0 mb-3 inline-flex items-center justify-center w-10 h-10 rounded-[5px] bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#C19A6B]"><MapPin className="w-5 h-5" /></span>
                <div className="min-w-0">
                  <p className="calli text-2xl text-[#C19A6B] leading-none">{t("geo_title")}</p>
                  <p className="text-sm text-zinc-200 mt-1.5 leading-relaxed">{t("geo_text")}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2.5">
                <button onClick={askGeo} data-testid="geo-accept" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[5px] bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-medium uppercase tracking-wider px-6 py-3 transition-colors">
                  <MapPin className="w-4 h-4" /> {t("geo_accept")}
                </button>
                <button onClick={dismissGeo} data-testid="geo-later" className="inline-flex items-center justify-center rounded-[5px] border border-white/20 hover:bg-white/10 text-zinc-300 text-xs uppercase tracking-wider px-4 py-3 transition-colors">{t("geo_later")}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AcquereurModal open={acqOpen} onClose={() => setAcqOpen(false)} />
    </PlatformLayout>
  );
}
